// The student's FINAL REPORT addendum at the end of an assignment-linked Writing Path essay: the within-essay developmental
// analysis (Writing + Organization of Thinking, from stored WSR/IST rows) and every valid across-essay point. The analyses are
// produced in the background right after Finish, so this polls until they arrive. Read-only; reuses the existing presentation.
import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { cMyDevelopmentBySession } from "@/lib/api";
import DevelopmentReport from "@/components/DevelopmentReport";
import { LongitudinalRecord } from "@/components/MyDevelopment";

const POLL_MS = 15000;
const MAX_POLLS = 40;   // ~10 minutes

export default function FinalDevelopmentReport({ sessionId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [polls, setPolls] = useState(0);
  const timer = useRef(null);

  const ready = (d) => !!(d?.has_development_data && d?.thinking?.has_thinking_data);

  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const d = await cMyDevelopmentBySession(sessionId);
        if (!live) return;
        setData(d);
        if (!ready(d) && polls < MAX_POLLS) timer.current = setTimeout(() => setPolls((n) => n + 1), POLL_MS);
      } catch (_) { if (live) setErr(true); }
    };
    load();
    return () => { live = false; clearTimeout(timer.current); };
  }, [sessionId, polls]);

  if (err) return null;
  const pending = !ready(data);
  return (
    <section data-testid="final-development-report" className="mt-8 border-t border-stone-200 pt-6">
      <p className="font-mono-panel text-[10px] uppercase tracking-[0.22em] text-stone-500">Your development · this essay and over time</p>
      <h2 className="mt-1 font-serif-display text-2xl text-stone-900">My Development</h2>
      <p className="mt-1 text-[13px] leading-relaxed text-stone-600">How your writing and the organization of your thinking developed. These describe your development — they are not grades.</p>
      {pending && (
        <p data-testid="final-development-pending" className="mt-3 inline-flex items-center gap-2 text-[13px] text-stone-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          {polls >= MAX_POLLS ? "Part of your analysis is still being prepared — it will be here when you come back to this essay." : "Your analysis is being prepared — it usually takes a few minutes. You can stay on this page or come back later."}
        </p>
      )}
      {data && (data.has_development_data || data.thinking?.has_thinking_data) && <div className="mt-4"><DevelopmentReport data={data} /></div>}
      <LongitudinalRecord minStarted={1} />
    </section>
  );
}
